/*
==================================================
CART MOBILE MODAL

Функция:
главный orchestrator mobile cart modal
с поддержкой шагов shipping / payment / review

Родитель:
layout / cart trigger

Дочерние элементы:
cart-mobile-shell
cart-mobile-body
cart-mobile-items-overlay

Использует:
step state
promo state
address controller bridge
payment methods bridge
sync query bridge для shipping / payment / review

Примечания:
после shipping шаг payment открывается отдельно
Next в footer программно нажимает внутренние кнопки checkout
финальный шаг review завершает заказ через placeOrder
==================================================
*/

"use client"

import { useEffect, useRef, useState } from "react"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { HttpTypes } from "@medusajs/types"

import CartMobileShell from "./cart-mobile-shell"
import CartMobileBody from "./cart-mobile-body"
import CartMobileItemsOverlay from "./cart-mobile-items-overlay"

import { listCartPaymentMethods } from "@lib/data/payment"
import { placeOrder } from "@lib/data/cart"

type Step = "cart" | "address" | "shipping" | "payment" | "review"

type AddressStepController = {
  submit: () => void
  reset?: () => void
}

export default function CartMobileModal({
  open,
  onClose,
  cart,
  customer = null,
  shippingOptions = [],
}: {
  open: boolean
  onClose: () => void
  cart?: HttpTypes.StoreCart | null
  customer?: HttpTypes.StoreCustomer | null
  shippingOptions?: HttpTypes.StoreCartShippingOption[]
}) {
  const [step, setStep] = useState<Step>("cart")
  const [promoOpen, setPromoOpen] = useState(false)
  const [itemsOnlyOpen, setItemsOnlyOpen] = useState(false)
  const [isSubmittingAddress, setIsSubmittingAddress] = useState(false)
  const [paymentMethods, setPaymentMethods] =
    useState<HttpTypes.StorePaymentProvider[]>([])

  const addressControllerRef = useRef<AddressStepController | null>(null)

  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const syncBrowserStep = (nextStep: Step) => {
    const params = new URLSearchParams(searchParams.toString())

    if (nextStep === "shipping") {
      params.set("step", "delivery")
    } else if (nextStep === "payment") {
      params.set("step", "payment")
    } else if (nextStep === "review") {
      params.set("step", "review")
    } else {
      params.delete("step")
    }

    const query = params.toString()
    router.replace(query ? `${pathname}?${query}` : pathname, {
      scroll: false,
    })
  }

  const clickInternalButton = (testId: string) => {
    const button = document.querySelector(
      `[data-testid="${testId}"]`
    ) as HTMLButtonElement | null

    if (!button || button.disabled) return false

    button.click()
    return true
  }

  useEffect(() => {
    async function loadPaymentMethods() {
      if (!cart?.region_id) return

      const methods = await listCartPaymentMethods(cart.region_id)
      setPaymentMethods(methods || [])
    }

    if (open) {
      loadPaymentMethods()
    }
  }, [open, cart?.region_id])

  useEffect(() => {
    if (!open) {
      setPromoOpen(false)
      setItemsOnlyOpen(false)
      setStep("cart")
      setIsSubmittingAddress(false)
      addressControllerRef.current?.reset?.()
      syncBrowserStep("cart")
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    syncBrowserStep(step)
  }, [step, open])

  const hasCartItems = !!cart?.items?.length
  const modalTitle = step === "cart" ? "CART" : "CHECKOUT"

  return (
    <>
      <CartMobileShell
        open={open}
        onClose={onClose}
        step={step}
        modalTitle={modalTitle}
        hasCartItems={hasCartItems}
        promoOpen={promoOpen}
        setPromoOpen={setPromoOpen}
        body={
          <CartMobileBody
            step={step}
            cart={cart}
            customer={customer}
            shippingOptions={shippingOptions}
            paymentMethods={paymentMethods}
            onOpenItemsOnly={() => {
              setItemsOnlyOpen(true)
            }}
            onRegisterAddressController={(controller) => {
              addressControllerRef.current = controller
            }}
            onAddressSubmittingChange={setIsSubmittingAddress}
            onAddressSaved={() => {
              setIsSubmittingAddress(false)
              setStep("shipping")
            }}
          />
        }
        footerProps={{
          step,
          hasCartItems,
          isSubmittingAddress,

          onCartCheckout: () => {
            setStep("address")
          },

          onAddressBack: () => {
            setIsSubmittingAddress(false)
            setStep("cart")
          },

          onAddressNext: () => {
            addressControllerRef.current?.submit()
          },

          onShippingBack: () => {
            setStep("address")
          },

          onShippingNext: () => {
            const clicked = clickInternalButton("submit-delivery-option-button")

            if (clicked) {
              setStep("payment")
              return
            }

            setStep("payment")
          },

          onPaymentBack: () => {
            setStep("shipping")
          },

          onPaymentNext: () => {
            const clicked = clickInternalButton("submit-payment-button")

            if (clicked) {
              setStep("review")
              return
            }

            setStep("review")
          },

          onReviewBack: () => {
            setStep("payment")
          },

          onReviewContinue: async () => {
            if (!cart?.id) return

            try {
              await placeOrder(cart.id)
            } catch (e) {
              console.error("placeOrder failed", e)
            }
          },
        }}
        cart={cart}
        shippingOptions={shippingOptions}
      />

      <CartMobileItemsOverlay
        open={open && itemsOnlyOpen}
        cart={cart ?? null}
        onClose={() => {
          setItemsOnlyOpen(false)
        }}
      />
    </>
  )
}
